/** Common permission checks used across the app */

export const isSuperUser = (user: MedScout.User) =>
  user?.is_superuser || user?.permissions?.is_support_user;

export const isCompanyMember = (user: MedScout.User, companyId: string) =>
  user?.is_superuser ||
  user?.permissions?.is_support_user ||
  (companyId && user?.company?.id === Number(companyId));

/**
 * RBAC code medscout-api requires on every integrations view — see
 * `INTEGRATION_CONFIG_PERMISSIONS` in the api's `integrations/permission.py`.
 */
export const INTEGRATION_CONFIG_READ = 'read.integration_config.company';

export const isIntegrationAdmin = (user: MedScout.User) =>
  user?.is_superuser ||
  user?.permissions?.is_support_user ||
  user?.all_permissions?.includes(INTEGRATION_CONFIG_READ) ||
  // Legacy fallback (PTRI-174): not every user has a role assigned yet, so the
  // boolean still grants access on its own. Drop once role coverage is complete.
  user?.is_integration_admin;

/**
 * RBAC code medscout-api requires to delete a company's integration config — see
 * `INTEGRATION_CONFIG_PERMISSIONS` in the api's `integrations/permission.py`. It also
 * covers unlinking an individual CRM record, which is a narrower version of the same
 * thing: deleting a mapping already removes every CRM link attached to it.
 */
export const INTEGRATION_CONFIG_DELETE = 'delete.integration_config.company';

/**
 * Whether the user may unlink a single CRM record from a profile. Deliberately not
 * `isIntegrationAdmin`: that passes on `INTEGRATION_CONFIG_READ`, which nearly every
 * system role holds — reps included — so the control would show to users the API then
 * refuses. No legacy `is_integration_admin` fallback either; the API won't accept it.
 */
export const canUnlinkCRMRecord = (user: MedScout.User) =>
  isSuperUser(user) || user?.all_permissions?.includes(INTEGRATION_CONFIG_DELETE);

/**
 * "Show reimbursement data on profiles" RBAC code. MKT-641 reuses it to gate the
 * Average Reimbursement insight on territory analytics, so a company or user with
 * reimbursement hidden on profiles has it hidden there too. Deliberately has no
 * superuser / support bypass, matching how the profile widgets check it.
 */
export const REIMBURSEMENT_PROFILE_SHOW = 'show.reimbursement.profile';

/**
 * RBAC code medscout-api requires on every territory-optimization route and on
 * the chat WebSocket — see `TERRITORY_OPTIMIZATION_PERMISSIONS` in the api's
 * `territory_optimization/permission.py`. One code gates the whole feature.
 * Replaces the `optimization-dashboard` LaunchDarkly flag (MKT-545).
 */
export const TERRITORY_OPTIMIZATION_ACCESS =
  'read.territory_optimization.company';

/**
 * Seat-type RBAC code medscout-api requires on every Strategy Center view
 * (target strategies, strategy chains, provider strategy tabs). Market
 * Intelligence seats don't have it (STR-936). No superuser / support bypass:
 * the API checks the code for everyone.
 */
export const TARGET_STRATEGIES_READ = 'read.target_strategies.personal';

/**
 * Seat-type RBAC code medscout-api requires on the plays and entity
 * profile-config views that drive profile customization (STR-936). No
 * superuser / support bypass: the API checks the code for everyone.
 */
export const PROFILE_CUSTOMIZATION_READ = 'read.profile_customization.company';
